import { Controller, useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { FormField } from "@/components/form/FormField";
import { FormCheckbox, FormInput, FormSelect } from "@/components/form/rhf";
import { Button } from "@/components/ui/button";
import { useUpdateStep } from "@/hooks/api/mutations/workflows.mutations";
import { useConnectors } from "@/hooks/api/queries/connectors.queries";
import type { WorkflowStep } from "@/types/workflow.types";
import { HighlightedCodeEditor } from "./highlighted-code-editor";

const optionalNumber = z.preprocess(
  (v) => (v === "" || v == null || Number.isNaN(v) ? undefined : Number(v)),
  z.number().int().positive().optional(),
);

const schema = z.object({
  connectorId: z.string().optional(),
  type: z.enum(["table", "sql", "mql"]),
  databaseName: z.string().optional(),
  schemaName: z.string().optional(),
  table: z.string().optional(),
  sql: z.string().optional(),
  collection: z.string().optional(),
  mql: z.string().optional(),
  sort: z.string().optional(),
  sortOrder: z.string().optional(),
  columns: z.string().optional(),
  where: z.string().optional(),
  limit: optionalNumber,
  batchSize: optionalNumber,
  maxRows: optionalNumber,
  initialSyncLimit: optionalNumber,
  initialSyncWhere: z.string().optional(),
  initialSyncOrder: z.string().optional(),
  incrementalEnabled: z.boolean(),
  incrementalField: z.string().optional(),
  incrementalType: z.enum(["timestamp", "integer"]),
});

type FormValues = z.infer<typeof schema>;
type FormInput = z.input<typeof schema>;

function toList(raw?: string) {
  return (raw ?? "").split(",").map((item) => item.trim()).filter(Boolean);
}

function toFormValues(step: WorkflowStep): FormInput {
  const config = step.config;
  const incremental = config.incremental as Record<string, unknown> | undefined;
  return {
    connectorId: step.connectorId ?? "",
    type: (["table", "sql", "mql"].includes(String(config.type)) ? config.type : "table") as FormValues["type"],
    databaseName: String(config.databaseName ?? ""),
    schemaName: String(config.schemaName ?? ""),
    table: String(config.table ?? config.tableName ?? ""),
    sql: String(config.sql ?? ""),
    collection: String(config.collection ?? ""),
    mql: String(config.mql ?? ""),
    sort: String(config.sort ?? ""),
    sortOrder: String(config.sortOrder ?? ""),
    columns: Array.isArray(config.columns) ? (config.columns as string[]).join(", ") : "",
    where: String(config.where ?? ""),
    limit: typeof config.limit === "number" ? config.limit : undefined,
    batchSize: typeof config.batchSize === "number" ? config.batchSize : undefined,
    maxRows: typeof config.maxRows === "number" ? config.maxRows : undefined,
    initialSyncLimit: typeof config.initialSyncLimit === "number" ? config.initialSyncLimit : undefined,
    initialSyncWhere: String(config.initialSyncWhere ?? ""),
    initialSyncOrder: String(config.initialSyncOrder ?? ""),
    incrementalEnabled: incremental?.enabled === true,
    incrementalField: String(incremental?.field ?? ""),
    incrementalType: incremental?.type === "integer" ? "integer" : "timestamp",
  };
}

function toPayload(step: WorkflowStep, values: FormValues): Record<string, unknown> {
  const incremental =
    step.config.incremental && typeof step.config.incremental === "object" && !Array.isArray(step.config.incremental)
      ? (step.config.incremental as Record<string, unknown>)
      : {};

  return {
    ...step.config,
    type: values.type,
    databaseName: values.databaseName || undefined,
    schemaName: values.schemaName || undefined,
    table: values.table || undefined,
    tableName: values.table || undefined,
    sql: values.sql || undefined,
    collection: values.collection || undefined,
    mql: values.mql || undefined,
    sort: values.sort || undefined,
    sortOrder: values.sortOrder || undefined,
    columns: toList(values.columns),
    where: values.where || undefined,
    limit: values.limit,
    batchSize: values.batchSize,
    maxRows: values.maxRows,
    initialSyncLimit: values.initialSyncLimit,
    initialSyncWhere: values.initialSyncWhere || undefined,
    initialSyncOrder: values.initialSyncOrder || undefined,
    incremental: values.incrementalEnabled
      ? { ...incremental, enabled: true, field: values.incrementalField || "", type: values.incrementalType }
      : { ...incremental, enabled: false },
  };
}

interface ExtractConfigFormProps {
  step: WorkflowStep;
  workflowId: string;
  onSuccess?: () => void;
}

export function ExtractConfigForm({ step, workflowId, onSuccess }: ExtractConfigFormProps) {
  const t = useTranslations("workflows");
  const updateStep = useUpdateStep();
  const { data: connectors } = useConnectors({ limit: 200, type: "credential" });
  const connectorOptions = [
    { value: "", label: t("drawer.placeholder.keep_connector") },
    ...(connectors?.data ?? []).map((connector) => ({
      value: connector.id,
      label: `${connector.name}${connector.subtype ? ` (${connector.subtype})` : ""}`,
    })),
  ];
  const methods = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(step),
  });
  const { control, handleSubmit, watch, formState: { errors, isSubmitting } } = methods;
  const type = watch("type");
  const connectorId = watch("connectorId");
  const incrementalEnabled = watch("incrementalEnabled");
  const selectedConnector = (connectors?.data ?? []).find((connector) => connector.id === connectorId);
  const connectorSubtype = selectedConnector?.subtype?.toLowerCase() ?? "";
  const hasMongoExtractFields = Boolean(step.config.collection || step.config.sort || step.config.sortOrder);
  const showMongoCollectionFields = type === "table" && (hasMongoExtractFields || connectorSubtype.includes("mongo"));

  const onSubmit = handleSubmit(async (values) => {
    await updateStep.mutateAsync({
      workflowId,
      stepId: step.id,
      config: toPayload(step, values),
      connectorId: values.connectorId || undefined,
    });
    onSuccess?.();
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <FormSelect name="connectorId" label={t("drawer.field.connector")} options={connectorOptions} />

        <div className="grid grid-cols-2 gap-3">
          <FormSelect
            name="type"
            label={t("drawer.field.extract_type")}
            options={[
              { value: "table", label: "Table" },
              { value: "sql", label: "SQL" },
              { value: "mql", label: "MQL" },
            ]}
          />
          <FormInput name="databaseName" label={t("drawer.field.database_name")} />
        </div>

        {type === "table" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <FormInput name="schemaName" label={t("drawer.field.schema_name")} />
              <FormInput name="table" label={t("drawer.field.table_name")} />
            </div>
            {showMongoCollectionFields && (
              <>
                <FormInput name="collection" label={t("drawer.field.collection")} />
                <div className="grid grid-cols-2 gap-3">
                  <FormInput name="sort" label={t("drawer.field.sort")} />
                  <FormSelect
                    name="sortOrder"
                    label={t("drawer.field.sort_order")}
                    options={[
                      { value: "", label: t("drawer.placeholder.not_set") },
                      { value: "asc", label: "asc" },
                      { value: "desc", label: "desc" },
                    ]}
                  />
                </div>
              </>
            )}
          </>
        )}
        {type === "sql" && (
          <FormField label={t("drawer.field.sql")} error={errors.sql?.message}>
            <Controller
              control={control}
              name="sql"
              render={({ field }) => (
                <HighlightedCodeEditor
                  language="sql"
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value)}
                  onBlur={field.onBlur}
                  rows={6}
                  minHeight={150}
                  invalid={!!errors.sql}
                />
              )}
            />
          </FormField>
        )}
        {type === "mql" && (
          <>
            <FormField label={t("drawer.field.mql")} error={errors.mql?.message}>
              <Controller
                control={control}
                name="mql"
                render={({ field }) => (
                  <HighlightedCodeEditor
                    language="json"
                    value={field.value ?? ""}
                    onChange={(event) => field.onChange(event.target.value)}
                    onBlur={field.onBlur}
                    rows={6}
                    minHeight={150}
                    invalid={!!errors.mql}
                  />
                )}
              />
            </FormField>
          </>
        )}

        {type !== "mql" && (
          <>
            <FormInput name="columns" label={t("drawer.field.columns")} hint={t("drawer.hint.comma_list")} />
            <FormInput name="where" label={t("drawer.field.where")} />
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <FormInput name="limit" type="number" label={t("drawer.field.limit")} />
          <FormInput name="batchSize" type="number" label={t("drawer.field.batch_size")} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormInput name="maxRows" type="number" label={t("drawer.field.max_rows")} />
          <FormInput name="initialSyncLimit" type="number" label={t("drawer.field.initial_sync_limit")} />
        </div>
        <FormInput name="initialSyncWhere" label={t("drawer.field.initial_sync_where")} />
        <FormSelect
          name="initialSyncOrder"
          label={t("drawer.field.initial_sync_order")}
          options={[
            { value: "", label: t("drawer.placeholder.not_set") },
            { value: "asc", label: "asc" },
            { value: "desc", label: "desc" },
          ]}
        />

        <FormCheckbox name="incrementalEnabled" label={t("drawer.field.incremental")} />
        {incrementalEnabled && (
          <div className="grid grid-cols-2 gap-3">
            <FormInput name="incrementalField" label={t("drawer.field.incremental_field")} />
            <FormSelect
              name="incrementalType"
              label={t("drawer.field.incremental_type")}
              options={[
                { value: "timestamp", label: "timestamp" },
                { value: "integer", label: "integer" },
              ]}
            />
          </div>
        )}

        {updateStep.isError && <p className="text-xs text-[var(--destructive)]">{t("drawer.save_error")}</p>}
        <Button type="submit" variant="primary" size="sm" disabled={isSubmitting || updateStep.isPending} className="w-full">
          {isSubmitting || updateStep.isPending ? t("drawer.saving") : t("drawer.save")}
        </Button>
      </form>
    </FormProvider>
  );
}

export default ExtractConfigForm;
